import type { ReportLayoutPolicy, ReportStyle } from '../../lib/api/reports.gen';
import { REPORT_PAGE_SIZE_MM, type ReportPageSize, type ReportPageOrientation } from './reportPageFormat';
import { LETTERHEAD_RESERVE_MM } from '../paginate';

// Report layout policy — the typed rendering adapter (P06 §4, story 4-107).
// Frontend counterpart of `backend/src/services/pdf/reportLayoutPolicy.js`.
//
// Story 4-128f: this used to be described as a "hand-maintained twin", citing
// "the same documented pattern `pdfExport.service.js`'s own `FONT_STACKS`/
// `TOKENS` already use for this exact report-rendering family". That family of
// twins is gone — `pdfExport.service.js` no longer renders anything, and the
// document's paper rules live once, in `../styles/document.css`.
//
// What is left here is NOT a second renderer: real API-sourced `ReportContent`
// already carries `layoutPolicy` resolved by the BACKEND
// (`report.service.js#buildRenderContent`), and `paginate.ts` generates the
// `@page` rule from whatever arrives. This resolver's one real consumer is
// `reportPreviewContent.ts`, which builds the template editor's DRAFT
// `ReportContent` client-side and has no server round-trip to get a
// `layoutPolicy` from. It is the OFFLINE-DRAFT case, not a parallel path for
// real content.

// The report's body margin — mirrors `reportLayoutPolicy.js`'s
// `BODY_MARGIN_MM`, not reinvented here. Story 4-114 — this is now the
// 'normal' margin PRESET's own value, not the only possible one; see
// `MARGIN_PRESETS_MM` below. (4-128f: both used to be described as
// hand-maintained twins of `pdfExport.service.js`; that file is deleted, and
// these values only ever reach paper through `paginate.ts`'s `@page` rule.)
const BODY_MARGIN_MM = { top: 16, right: 14, bottom: 20, left: 14 };

export const MARGIN_PRESETS = ['narrow', 'normal', 'wide'] as const;
export type MarginsPreset = (typeof MARGIN_PRESETS)[number];
export const MARGIN_PRESETS_MM: Record<MarginsPreset, { top: number; right: number; bottom: number; left: number }> = {
  narrow: { top: 12, right: 10, bottom: 16, left: 10 },
  normal: { ...BODY_MARGIN_MM },
  wide: { top: 22, right: 20, bottom: 26, left: 20 },
};
const resolveMarginsPreset = (preset: unknown): MarginsPreset =>
  MARGIN_PRESETS.includes(preset as MarginsPreset) ? (preset as MarginsPreset) : 'normal';
const marginsMmOf = (preset: unknown) => ({ ...MARGIN_PRESETS_MM[resolveMarginsPreset(preset)] });

// Story 4-114 — same real-numbers calibration the backend twin documents
// (`reportLayoutPolicy.js#MIN_SAFE_CONTENT_HEIGHT_MM`'s own comment — only
// the widest margin preset in landscape with a repeating letterhead actually
// trips this at A4/letter scale); kept as the SAME literal so a margin
// preset that is unsafe on one side is unsafe on the other too.
const MIN_SAFE_CONTENT_HEIGHT_MM = 120;
// Puppeteer's own always-on `page.pdf({ margin: { bottom: '40px' } })`
// reservation, converted to mm — the same constant the backend's
// `FOOTER_RESERVE_MM` names (kept as a literal here rather than importing a
// backend module, per this whole file family's hand-maintained-twin rule).
const FOOTER_RESERVE_MM = 40 * (25.4 / 96);
// Story 4-128b — `LETTERHEAD_RESERVE_MM` now comes from `paginate.ts`, the ONE
// frontend owner (imported at the top of this file). Before this story this
// file and `PagedReportPreview.tsx` each carried their own `const
// LETTERHEAD_RESERVE_MM = 32`, so the number a margin-safety check used and
// the number the real `@page` rule spent were free to drift apart silently.

export const PAGE_ORIENTATIONS = ['portrait', 'landscape'] as const;
const resolveOrientation = (orientation: unknown): ReportPageOrientation =>
  orientation === 'landscape' ? 'landscape' : 'portrait';

const resolvePageFormat = (pageSize?: ReportPageSize | null): ReportPageSize =>
  pageSize === 'letter' ? 'letter' : 'A4';

const LETTERHEAD_MODES = ['none', 'firstContentPage', 'everyContentPage'] as const;
export type LetterheadMode = (typeof LETTERHEAD_MODES)[number];
const resolveLetterheadMode = (mode: unknown): LetterheadMode =>
  LETTERHEAD_MODES.includes(mode as LetterheadMode) ? (mode as LetterheadMode) : 'firstContentPage';

export function resolveReportLayoutPolicy(style: ReportStyle): ReportLayoutPolicy {
  return {
    pageFormat: resolvePageFormat(style.pageSize),
    orientation: resolveOrientation(style.pageOrientation),
    marginsMm: marginsMmOf(style.marginsPreset),
    frontMatter: {
      enabled: Boolean(style.coverPage),
      // Placeholder for `4-108` — nothing implements front-matter-exempt
      // body page numbering yet; see the contract's own comment.
      countsTowardPageNumbers: false,
    },
    letterhead: { mode: resolveLetterheadMode(style.letterhead?.mode) },
  };
}

/**
 * Story 4-114 — the real usable content height (mm) for a page/orientation/
 * margin/letterhead combination: physical page height minus both margins,
 * Puppeteer's own always-on footer reservation, and the letterhead reserve
 * when the letterhead repeats on every page. Shared by `marginsPresetIsSafe`
 * below (whether a PRESET is safe in general) and
 * `ReportStudioPanels.tsx`'s own "does THIS section's real measured height
 * fit on one page" check — the "avoid-split taller than one page" edge case
 * needs the same number, just compared against a real DOM measurement
 * instead of a hypothetical worst case.
 */
export function availableContentHeightMm(ctx: {
  pageSize: ReportPageSize;
  orientation?: ReportPageOrientation;
  marginsPreset: MarginsPreset;
  letterheadMode?: LetterheadMode;
}): number {
  const margins = marginsMmOf(ctx.marginsPreset);
  const size = REPORT_PAGE_SIZE_MM[ctx.pageSize] ?? REPORT_PAGE_SIZE_MM.A4;
  const height = ctx.orientation === 'landscape' ? size.width : size.height;
  const letterheadReserve = resolveLetterheadMode(ctx.letterheadMode) === 'everyContentPage' ? LETTERHEAD_RESERVE_MM : 0;
  return height - margins.top - margins.bottom - FOOTER_RESERVE_MM - letterheadReserve;
}

/**
 * Story 4-114 edge case — "header/footer consuming more than the safe
 * printable area": whether `preset` leaves enough real content room for the
 * given format/orientation/letterhead mode. IDENTICAL computation to the
 * backend twin (`reportLayoutPolicy.js#marginsPresetIsSafe`) so a control
 * disables the SAME options the backend would also consider unsafe.
 */
export function marginsPresetIsSafe(
  preset: MarginsPreset,
  ctx: { pageSize: ReportPageSize; orientation?: ReportPageOrientation; letterheadMode?: LetterheadMode },
): boolean {
  return availableContentHeightMm({ ...ctx, marginsPreset: preset }) >= MIN_SAFE_CONTENT_HEIGHT_MM;
}

// Re-exported so a caller that already imports this module for the policy
// resolver doesn't also need `reportPageFormat.ts` just for the raw mm table.
export { REPORT_PAGE_SIZE_MM };
